/**
 * Report API - PDF export functionality for widgets and dashboards
 */

import { useApiMutation, useApiQuery } from "@/hooks/use-api";
import { API_CROWLINGO_AUTHED, hasSelectedWorkspace } from "./core";

// ============================================================================
// Types
// ============================================================================

export type ReportType = "widget" | "dashboard" | "text";
export type ReportStatus = "pending" | "generating" | "generated" | "failed";
export type ReportQuality = "low" | "medium" | "high";
export type ReportLayout = "grid" | "pages";
export type PageFormat = "A4" | "Letter" | "Legal" | "Tabloid" | "A3" | "A5";

/**
 * BlockNote block type for rich text content in reports
 */
export interface ReportBlock {
  id?: string;
  type: string;
  props?: Record<string, unknown>;
  content?: unknown[];
  children?: ReportBlock[];
}

export interface PdfSettings {
  format: PageFormat;
  landscape: boolean;
  quality: ReportQuality;
  include_cover: boolean;
  layout: ReportLayout;
}

export interface CoverSettings {
  name: string;
  description?: string;
  logo_url?: string;
}

export interface Report {
  id: string;
  name: string;
  description?: string | null;
  report_type: ReportType;
  target_id?: string | null;
  /** BlockNote blocks for text-type reports */
  blocks?: ReportBlock[] | null;
  filters?: string | null;
  status: ReportStatus;
  file_url?: string | null;
  last_generated?: string | null;
  error_message?: string | null;
  cron?: string | null;
  cover?: CoverSettings | null;
  pdf_settings?: PdfSettings;
  channels?: unknown[];
}

export interface ReportCreate {
  name: string;
  description?: string;
  report_type: ReportType;
  /** Target ID for widget/dashboard reports (optional for text reports) */
  target_id?: string;
  /** BlockNote blocks for text-type reports */
  blocks?: ReportBlock[];
  filters?: string;
  cron?: string;
  // PDF settings
  format?: PageFormat;
  landscape?: boolean;
  quality?: ReportQuality;
  include_cover?: boolean;
  layout?: ReportLayout;
  // Cover settings
  cover_title?: string;
  cover_description?: string;
}

export interface ReportGenerateRequest {
  filters?: string;
}

export interface QuickExportRequest {
  filters?: string;
}

// ============================================================================
// Hooks for React Query
// ============================================================================

/**
 * Hook to list all reports in the workspace
 */
export const useReports = () =>
  useApiQuery<Report[]>(
    { url: "reports" },
    { enabled: hasSelectedWorkspace() }
  );

/**
 * Hook to get a single report by ID
 */
export const useReport = (id?: string) =>
  useApiQuery<Report>(
    { url: `reports/${id}` },
    { enabled: !!id && hasSelectedWorkspace() }
  );

/**
 * Hook to create a new report
 */
export const useCreateReport = () =>
  useApiMutation<Report, ReportCreate>({
    url: "reports",
    method: "POST",
  });

/**
 * Hook to delete a report
 */
export const useDeleteReport = () =>
  useApiMutation<{ status: string; id: string }, Record<string, never>, { id: string }>({
    url: "reports/{id}",
    method: "DELETE",
  });

/**
 * Hook to generate a report PDF
 */
export const useGenerateReport = () =>
  useApiMutation<Report, ReportGenerateRequest, { id: string }>({
    url: "reports/{id}/generate",
    method: "POST",
  });

// ============================================================================
// Quick Export Functions (one-off exports without creating a report)
// ============================================================================

/**
 * Quick export a widget to PDF
 * Returns a blob URL that can be used for download
 */
export const quickExportWidget = async (
  widgetId: string,
  options?: QuickExportRequest
): Promise<Blob> => {
  const response = await API_CROWLINGO_AUTHED.post(
    `widgets/${widgetId}/export-pdf`,
    options || {},
    { responseType: "blob" }
  );
  return response.data;
};

/**
 * Quick export a dashboard to PDF
 * Returns a blob URL that can be used for download
 */
export const quickExportDashboard = async (
  dashboardId: string,
  options?: QuickExportRequest
): Promise<Blob> => {
  const response = await API_CROWLINGO_AUTHED.post(
    `dashboards/${dashboardId}/export-pdf`,
    options || {},
    { responseType: "blob" }
  );
  return response.data;
};

/**
 * Hook for quick widget export
 */
export const useQuickExportWidget = () =>
  useApiMutation<Blob, QuickExportRequest, { widgetId: string }>({
    url: "widgets/{widgetId}/export-pdf",
    method: "POST",
  });

/**
 * Hook for quick dashboard export
 */
export const useQuickExportDashboard = () =>
  useApiMutation<Blob, QuickExportRequest, { dashboardId: string }>({
    url: "dashboards/{dashboardId}/export-pdf",
    method: "POST",
  });

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Download a blob as a file
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Download a report by opening its file URL
 */
export const downloadReport = async (report: Report): Promise<void> => {
  if (!report.file_url) {
    throw new Error("Report file not available");
  }

  // Fetch the PDF from the backend
  const response = await API_CROWLINGO_AUTHED.get(
    `reports/${report.id}/download`,
    { responseType: "blob" }
  );

  const filename = `${report.name.replace(/\s+/g, "_")}.pdf`;
  downloadBlob(response.data, filename);
};

/**
 * Format a cron expression for display
 */
export const formatCronExpression = (cron: string): string => {
  const parts = cron.split(" ");
  if (parts.length !== 5) return cron;

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Simple patterns
  if (dayOfWeek === "*" && dayOfMonth === "*" && month === "*") {
    if (minute === "0" && hour !== "*") {
      return `Tous les jours à ${hour}h`;
    }
  }

  if (dayOfWeek !== "*" && dayOfMonth === "*" && month === "*") {
    const days = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
    const dayName = days[parseInt(dayOfWeek, 10)] || dayOfWeek;
    if (minute === "0") {
      return `Chaque ${dayName} à ${hour}h`;
    }
  }

  if (dayOfMonth !== "*" && month === "*") {
    if (minute === "0") {
      return `Le ${dayOfMonth} de chaque mois à ${hour}h`;
    }
  }

  return cron;
};

/**
 * Common cron presets for scheduling
 */
export const CRON_PRESETS = [
  { label: "Tous les jours à 9h", value: "0 9 * * *" },
  { label: "Tous les lundis à 9h", value: "0 9 * * 1" },
  { label: "Le 1er de chaque mois à 9h", value: "0 9 1 * *" },
  { label: "Tous les jours à 18h", value: "0 18 * * *" },
  { label: "Tous les vendredis à 17h", value: "0 17 * * 5" },
] as const;
